import type { ReactNode } from "react";
import type { AsyncState } from "../app/use-async.js";
import { ApiError, refusedAction } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import type { UnreadSummary } from "../api/types.js";
import { SEVERITY_MARK, SEVERITY_TONE, SEVERITY_WORD } from "../lib/comms.js";

/**
 * The two counts in the masthead: unread messages, and unread notifications.
 *
 * Three decisions worth writing down, because each of them is a way this could
 * quietly mislead somebody.
 *
 * An absent badge means zero, and that convention is only safe if a badge that
 * could not be counted never looks absent. So a refusal and a failure both
 * render, in words — "your role may not read chat" and "count unavailable" —
 * rather than falling back to silence, which a reader would take for nought.
 *
 * The notification badge carries the worst severity as a word and a shape
 * beside the number, not as a colour. A red 3 and an amber 3 are the same badge
 * on a monochrome print-out and to a reader who cannot tell the two apart, and
 * the difference between "three reminders" and "a compliance window has closed
 * unsatisfied" is the whole reason for the count.
 *
 * `highestSeverity` is taken from the API rather than worked out from the feed.
 * The masthead does not load the feed at all, and a badge derived from a
 * filtered list would disagree with the hub in the one direction that matters.
 */

/** Unread direct and job messages. Just the number: chat has no severity. */
export function ChatUnreadBadge(): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync<number>(() => client.chatUnread(), [client]);

  return (
    <Badge state={state} what="chat">
      {(total) =>
        total === 0 ? null : (
          <span className="status-word status-approaching">
            <span aria-hidden="true">◑ </span>
            {total} unread
          </span>
        )
      }
    </Badge>
  );
}

/** Unread notifications, with the worst severity among them said out loud. */
export function NotificationUnreadBadge(): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync<UnreadSummary>(
    () => client.notificationUnread(),
    [client],
  );

  return (
    <Badge state={state} what="notifications">
      {(summary) =>
        summary.total === 0 || summary.highestSeverity === null ? null : (
          <span
            className={`status-word status-${SEVERITY_TONE[summary.highestSeverity]}`}
          >
            <span aria-hidden="true">{SEVERITY_MARK[summary.highestSeverity]} </span>
            {summary.total} unread, worst{" "}
            {SEVERITY_WORD[summary.highestSeverity].toLowerCase()}
          </span>
        )
      }
    </Badge>
  );
}

/**
 * The three states a count can be in, with silence reserved for one of them.
 *
 * Loading renders nothing, which does briefly read as zero — the honest cost of
 * not putting a spinner in a navigation bar, and it resolves in one round trip.
 * Refused and failed never do: they say which, in words, because a badge that
 * vanishes on a 403 teaches its reader that they have no unread messages when
 * what has actually happened is that nobody will tell them.
 */
function Badge<T>({
  state,
  what,
  children,
}: {
  readonly state: AsyncState<T>;
  readonly what: string;
  readonly children: (data: T) => ReactNode;
}): ReactNode {
  if (state.status === "loading") return null;
  if (state.status === "ready") return children(state.data);

  const refused =
    state.error instanceof ApiError ? refusedAction(state.error) : null;
  if (refused) {
    return (
      <span className="muted small">
        (your role may not {refused.action} {refused.resource})
      </span>
    );
  }
  return <span className="muted small">({what} count unavailable)</span>;
}
